import type {
  StaffingCoverage,
  BprSolutionCandidateBatch,
  CustomerDocument,
  CustomerDocumentDetail,
  DealResourcePlan,
  DealSow,
  DealSowGenerationResult,
  EstimateVersionSummary,
  JiraSyncSummary,
  JiraTicketPlan,
  ReferenceEstimateSet,
  ResourcePlanPricingBasis,
  ScopeBriefKey,
  ScopeMeta,
  SalesCompanySummary,
  SalesDealAnswerInput,
  SalesDealAvailableStructure,
  SalesDealEstimateResult,
  SalesDealMeetingContext,
  SalesDealQuestionnaireSummary,
  SalesDealSummary,
  SavedDealEstimate,
  DealCategoryOption,
  DealDocActivityGroup,
  SowReadinessReport,
  SowRenderContext,
  SowVersionSummary,
  WorkspaceProductSummary,
} from '@protoss/shared';
import { apiFetch } from '../../../lib/apiClient';

/** A selectable workspace role for the resource-plan role multi-select. */
export interface ResourcePlanAvailableRole {
  role_id: string;
  role_name: string;
  level: string | null;
  category: string | null;
}

export class SowReadinessError extends Error {
  readonly readinessReport: SowReadinessReport;
  constructor(report: SowReadinessReport) {
    super('SOW_MISSING_REQUIRED_FIELDS');
    this.name = 'SowReadinessError';
    this.readinessReport = report;
  }
}
export type {
  ConditionalRule,
  SalesCompanySummary,
  SalesDealAnswerInput,
  SalesDealQuestionnaireSummary,
  SalesDealSummary,
  SalesDealTemplateFieldSummary,
  SalesDealTemplateGroupSummary,
  SalesDealTemplateOption,
  SalesDealTemplateSectionSummary,
  VendorProductSummary,
  WorkspaceProductSummary,
} from '@protoss/shared';

/** A reference-estimate corpus doc backing a grounded estimate figure (the [N] dialog). */
export interface ReferenceEstimateDetail {
  documentUuid: string;
  title: string;
  /** Human provenance label ("Company - Deal", else filename, else title) - the dialog heading. */
  label: string;
  set: ReferenceEstimateSet;
}

/** One parsed scope row as the confirm step renders it (see docs/plans/27). */
export interface ScopeFilePreviewRow {
  sourceRow: number;
  label: string;
  section?: string;
  values: Record<string, string>;
}

/** Server response for the no-write parse step: what we detected, and what we ignored. */
export interface ScopeFileParseResponse {
  sheetName: string;
  sheetIndex: number;
  sheetCount: number;
  headerRow: number;
  headers: string[];
  mapping: Record<string, number>;
  confidence: 'high' | 'low';
  itemCount: number;
  sectionCount: number;
  skippedCount: number;
  /** Items with no business process - the key the estimate splits its sections on. */
  itemsWithoutBusinessProcess: number;
  /** Every sheet in the workbook that parses, so the user can switch sheets. */
  sheetOptions: Array<{
    sheetIndex: number;
    sheetName: string;
    itemCount: number;
    withBusinessProcess: number;
  }>;
  ignoredHoursColumns: string[];
  unmappedColumns: string[];
  previewRows: ScopeFilePreviewRow[];
  /** PRIMARY scope rows already on the deal - kept and costed alongside the import. */
  existingScopeItemCount: number;
}

export interface ScopeFileImportResponse {
  /** The uploaded sheet, attached to the deal so it appears under Docs. Null if attaching failed. */
  docUuid: string | null;
  importedItemCount: number;
  replacedItemCount: number;
  keptItemCount: number;
  fieldKeys: string[];
  sheetName: string;
}

export const salesApi = {
  /** Fetch a reference-estimate corpus doc by uuid (estimate provenance dialog). */
  async getReferenceEstimate(documentUuid: string): Promise<ReferenceEstimateDetail> {
    const res = await apiFetch<{ data: ReferenceEstimateDetail }>(
      `/api/sales/reference-estimates/${encodeURIComponent(documentUuid)}`
    );
    return res.data;
  },

  /** Re-index all existing reference corpus docs for the workspace (admin only). */
  async reindexReferenceCorpus(): Promise<{ total: number; reindexed: number }> {
    const res = await apiFetch<{ data: { total: number; reindexed: number } }>(
      '/api/sales/reference-corpus/reindex',
      { method: 'POST' }
    );
    return res.data;
  },

  async getCompanySuggestions(): Promise<{ companies: string[] }> {
    return await apiFetch('/api/sales/deals/company-suggestions');
  },

  async getFieldValuesBySource(source: string): Promise<{ values: string[] }> {
    const params = new URLSearchParams({ source });
    return await apiFetch(`/api/sales/deals/field-values?${params.toString()}`);
  },

  async addressAutocomplete(
    q: string
  ): Promise<{ values: string[]; placeIds: Record<string, string> }> {
    const params = new URLSearchParams({ q });
    return await apiFetch(`/api/sales/deals/address-autocomplete?${params.toString()}`);
  },

  async addressDetails(placeId: string): Promise<{
    street: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
  }> {
    const params = new URLSearchParams({ placeId });
    return await apiFetch(`/api/sales/deals/address-details?${params.toString()}`);
  },

  async customerByEmail(email: string): Promise<{
    customer: {
      name: string | null;
      title: string | null;
      department: string | null;
      emailAlt: string | null;
      phone: string | null;
      phoneAlt: string | null;
    } | null;
  }> {
    const params = new URLSearchParams({ email });
    return await apiFetch(`/api/sales/deals/customer-by-email?${params.toString()}`);
  },

  async getWorkspaceProducts(): Promise<{ products: WorkspaceProductSummary[] }> {
    return await apiFetch('/api/sales/workspace-products');
  },

  async listDeals(): Promise<{ deals: SalesDealSummary[] }> {
    return await apiFetch('/api/sales/deals');
  },

  async softDeleteDeals(dealUuids: string[]): Promise<{ data: { deleted: number } }> {
    return await apiFetch('/api/sales/deals', {
      method: 'DELETE',
      body: JSON.stringify({ dealUuids }),
    });
  },

  async listCompanies(): Promise<{ companies: SalesCompanySummary[] }> {
    return await apiFetch('/api/sales/companies');
  },

  async getCompanyDeals(
    companyName: string,
    workspaceProductId?: number | null,
    categoryKey?: string | null
  ): Promise<{ deals: SalesDealSummary[]; moduleRole: string }> {
    const params = new URLSearchParams({ companyName });
    if (workspaceProductId != null) params.set('workspaceProductId', String(workspaceProductId));
    if (categoryKey != null) params.set('categoryKey', categoryKey);
    return await apiFetch(`/api/sales/deals/draft?${params.toString()}`);
  },

  async startDeal(
    companyName: string,
    workspaceProductId?: number | null,
    categoryKey?: string | null,
    forceCreate?: boolean
  ): Promise<{ deal: SalesDealSummary }> {
    return await apiFetch('/api/sales/deals/start', {
      method: 'POST',
      body: JSON.stringify({
        name: companyName,
        workspaceProductId: workspaceProductId ?? undefined,
        categoryKey: categoryKey ?? undefined,
        forceCreate: forceCreate ?? undefined,
      }),
    });
  },

  async startFreshDeal(
    companyName: string,
    workspaceProductId?: number | null,
    categoryKey?: string | null
  ): Promise<{ deal: SalesDealSummary }> {
    return await apiFetch('/api/sales/deals/start-fresh', {
      method: 'POST',
      body: JSON.stringify({
        name: companyName,
        workspaceProductId: workspaceProductId ?? undefined,
        categoryKey: categoryKey ?? undefined,
      }),
    });
  },

  async getCategoryOptions(
    workspaceProductId?: number | null
  ): Promise<{ options: DealCategoryOption[] }> {
    if (workspaceProductId == null) return { options: [] };
    const params = new URLSearchParams({ workspaceProductId: String(workspaceProductId) });
    return await apiFetch(`/api/sales/deals/category-options?${params.toString()}`);
  },

  async getAttributeOptions(workspaceProductId?: number | null): Promise<{
    byKey: Record<string, { valueKey: string; label: string; displayOrder: number }[]>;
  }> {
    if (workspaceProductId == null) return { byKey: {} };
    const params = new URLSearchParams({ workspaceProductId: String(workspaceProductId) });
    return await apiFetch(`/api/sales/deals/attribute-options?${params.toString()}`);
  },

  async startDealFromScript(
    companyName: string,
    file: File,
    workspaceProductId?: number | null,
    categoryKey?: string | null
  ): Promise<{ deal: SalesDealSummary; docUuid: string | null; warning?: string }> {
    const formData = new FormData();
    formData.append('companyName', companyName);
    formData.append('file', file, file.name);
    if (workspaceProductId != null) {
      formData.append('workspaceProductId', String(workspaceProductId));
    }
    // Forward the chosen category so a transcript deal resolves the same category-scoped
    // template/profiles as a manual deal (additive new categories included).
    if (categoryKey) {
      formData.append('categoryKey', categoryKey);
    }
    return await apiFetch('/api/sales/deals/start-from-script', {
      method: 'POST',
      body: formData,
    });
  },

  async uploadDealTranscript(
    dealUuid: string,
    file: File,
    /** Bypass dedup and create a fresh copy (the "add a fresh copy anyway" override). */
    force = false
  ): Promise<{
    docUuid: string;
    reused?: boolean;
    duplicateOf?: { title: string };
    /** Set when the file is longer than discovery will read (additive; the file is not cut). */
    warning?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const qs = force ? '?force=true' : '';
    return await apiFetch(`/api/sales/deals/${dealUuid}/transcript${qs}`, {
      method: 'POST',
      body: formData,
    });
  },

  // Google Drive equivalents of the two transcript-upload calls above: the server downloads the
  // picked Drive file and runs the same ingest, so the client only sends the file reference.
  async startDealFromScriptFromDrive(
    companyName: string,
    integrationUuid: string,
    file: { id: string; name: string; mimeType: string },
    workspaceProductId?: number | null,
    categoryKey?: string | null
  ): Promise<{ deal: SalesDealSummary; docUuid: string | null; warning?: string }> {
    return await apiFetch('/api/sales/deals/start-from-script-from-drive', {
      method: 'POST',
      body: JSON.stringify({
        companyName,
        integrationUuid,
        file,
        workspaceProductId: workspaceProductId ?? null,
        categoryKey: categoryKey ?? null,
      }),
    });
  },

  async uploadDealTranscriptFromDrive(
    dealUuid: string,
    integrationUuid: string,
    file: { id: string; name: string; mimeType: string },
    /** Bypass dedup and create a fresh copy (the "add a fresh copy anyway" override). */
    force = false
  ): Promise<{
    docUuid: string;
    reused?: boolean;
    duplicateOf?: { title: string };
    /** Set when the file is longer than discovery will read (additive; the file is not cut). */
    warning?: string;
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/transcript-from-drive`, {
      method: 'POST',
      body: JSON.stringify({ integrationUuid, file, force }),
    });
  },

  /**
   * Save the user's optional "Additional notes" (1-4000 chars) on the deal as a text document. It
   * never counts as a transcript and does not start any analysis by itself.
   */
  async saveDealAdditionalNotes(dealUuid: string, text: string): Promise<{ docUuid: string }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/additional-notes`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async getDeal(dealUuid: string): Promise<{ deal: SalesDealSummary; moduleRole: string }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}`);
  },

  async triggerEnrichment(dealUuid: string): Promise<{ runUuid: string }> {
    return await apiFetch<{ runUuid: string }>(`/api/sales/deals/${dealUuid}/enrich`, {
      method: 'POST',
    });
  },

  async getEnrichmentRunStatus(runUuid: string): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    resultSummary: string | null;
    errorMessage: string | null;
    completedAt: string | null;
  }> {
    return await apiFetch(`/api/sales/deals/enrich-runs/${runUuid}`);
  },

  async getDealQuestionnaire(
    dealUuid: string
  ): Promise<{ questionnaire: SalesDealQuestionnaireSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/questionnaire`);
  },

  /**
   * Apply a harvested Scope-doc save (see scope-doc-harvest.ts) in ONE transactional request:
   * answer upserts + scope-brief replacements + BP re-tags + new scope items + item order.
   * Returns the refreshed questionnaire.
   */
  async applyScopeDocument(
    dealUuid: string,
    payload: import('../utils/scope-doc-harvest').ScopeDocumentPayload
  ): Promise<{ questionnaire: SalesDealQuestionnaireSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/questionnaire-document`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  // Additive deals: the catalog of sections/groups available to add, each flagged included.
  async getDealAvailableStructure(
    dealUuid: string
  ): Promise<{ structure: SalesDealAvailableStructure }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/available-structure`);
  },

  // Additive deals: add (include) and/or remove (exclude) questionnaire groups by uuid.
  // Returns the refreshed questionnaire so the caller can re-render in one round-trip.
  async setDealGroupInclusion(
    dealUuid: string,
    input: {
      include?: string[];
      exclude?: string[];
      includeFields?: string[];
      excludeFields?: string[];
    }
  ): Promise<{ questionnaire: SalesDealQuestionnaireSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/groups`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async confirmQuestionnaireField(dealUuid: string, fieldKey: string): Promise<void> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/questionnaire/fields/${encodeURIComponent(fieldKey)}/confirm`,
      { method: 'POST' }
    );
  },

  async rejectQuestionnaireField(dealUuid: string, fieldKey: string): Promise<void> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/questionnaire/fields/${encodeURIComponent(fieldKey)}/reject`,
      { method: 'POST' }
    );
  },

  async saveDealQuestionnaire(
    dealUuid: string,
    answers: SalesDealAnswerInput[]
  ): Promise<{ questionnaire: SalesDealQuestionnaireSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/questionnaire`, {
      method: 'PUT',
      body: JSON.stringify({ answers }),
    });
  },

  /** Capture AI-discovered one-time questions onto a deal (creates deal_adhoc_field rows). */
  async captureAdhocFields(
    dealUuid: string,
    questions: Array<{
      label: string;
      fieldKey?: string;
      uiControl: string;
      options?: Array<{ label: string; value: string }>;
      suggestedValue?: string | null;
      estimateHours?: number | null;
      grounding?: unknown;
      scopeMeta?: ScopeMeta;
      /** Target group's primary fieldKey when adding a field inside a group; omit for a new group. */
      groupKey?: string;
    }>
  ): Promise<{ created: Array<{ uuid: string; fieldKey: string; label: string }> }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/adhoc-fields`, {
      method: 'POST',
      body: JSON.stringify({ questions }),
    });
  },

  /**
   * Add a plain questionnaire field to an existing (sectionKey, groupKey) of the deal's template
   * (fork-on-edit, copy-on-write). Unlike captureAdhocFields this renders in the group's main pane
   * and is never a scope/estimate item. With aiEnabled the AI tries to fill it during extraction.
   */
  async addTemplateField(
    dealUuid: string,
    body: {
      sectionKey: string;
      groupKey: string;
      /** When set, the (sectionKey, groupKey) group is created-or-revived before the field insert. */
      groupLabel?: string;
      /**
       * 'deal_only' (default) keeps the field private to this deal; 'reusable' adds it to the
       * workspace template so future deals get it (server re-checks template-edit permission).
       */
      scope?: 'deal_only' | 'reusable';
      draft: {
        label: string;
        uiControl: string;
        options?: Array<{ label: string; value: string }>;
        aiEnabled?: boolean;
      };
    }
  ): Promise<{ field: { uuid: string; fieldKey: string; label: string } }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/template/fields`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Remove a question previously added to the deal's questionnaire. scope='deal_only' (default)
   * removes it from THIS deal only; scope='reusable' also removes it from the workspace template
   * so future deals stop getting it (server re-checks template-edit permission).
   */
  async deleteTemplateField(
    dealUuid: string,
    fieldKey: string,
    scope: 'deal_only' | 'reusable' = 'deal_only'
  ): Promise<void> {
    await apiFetch(
      `/api/sales/deals/${dealUuid}/template/fields/${encodeURIComponent(fieldKey)}?scope=${scope}`,
      { method: 'DELETE' }
    );
  },

  /**
   * Apply review-card decisions for AI-discovered questions in one call: 'new' items are inserted
   * as ad-hoc fields; 'update' items overwrite an existing template/ad-hoc answer (+ ad-hoc scope).
   */
  async applyDiscoveredQuestions(
    dealUuid: string,
    items: Array<{
      label: string;
      fieldKey?: string;
      uiControl: string;
      options?: Array<{ label: string; value: string }>;
      suggestedValue?: string | null;
      scopeMeta?: ScopeMeta;
      target?: 'new' | 'update';
      existingFieldKey?: string;
      existingKind?: 'template' | 'adhoc';
      templateFieldId?: number;
    }>,
    /** Discovered items the user reviewed but un-checked - recorded as a flywheel reject signal. */
    rejected?: Array<{
      label: string;
      scopeMeta?: ScopeMeta | null;
      strategicScope?: string | null;
    }>
  ): Promise<{ created: number; updated: number }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/discovered-questions/apply`, {
      method: 'POST',
      body: JSON.stringify({ items, ...(rejected && rejected.length > 0 ? { rejected } : {}) }),
    });
  },

  /** Update a captured question's scope interpretation (work-type / scope-status / drivers). */
  async updateAdhocScopeMeta(
    dealUuid: string,
    fieldKey: string,
    scopeMeta: ScopeMeta
  ): Promise<void> {
    await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/scope-meta`,
      { method: 'PATCH', body: JSON.stringify(scopeMeta) }
    );
  },

  /**
   * Shallow-merge ONLY the always-saved scope fields (scopeStatus / priorities) - used by the
   * immediate-persist controls in both panes so a toggle never clobbers staged brief/workType edits.
   */
  async mergeAdhocScopeMeta(
    dealUuid: string,
    fieldKey: string,
    patch: Partial<
      Pick<
        ScopeMeta,
        | 'scopeStatus'
        | 'customerPriority'
        | 'recommendedPriority'
        | 'businessProcess'
        | 'workType'
        | 'inScope'
        | 'outOfScope'
        | 'complexityDrivers'
        | 'assumptions'
      >
    >
  ): Promise<void> {
    await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/scope-meta/merge`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    );
  },

  /** Rename a coarse group: re-tag every member's businessProcess (oldName -> newName). */
  async renameAdhocGroup(dealUuid: string, oldName: string, newName: string): Promise<void> {
    await apiFetch(`/api/sales/deals/${dealUuid}/adhoc-groups/rename`, {
      method: 'PATCH',
      body: JSON.stringify({ oldName, newName }),
    });
  },

  /** Derived complexity-driver suggestions (the library) for the deal's category. */
  async getScopeDriverSuggestions(dealUuid: string): Promise<string[]> {
    const res = await apiFetch<{ suggestions: string[] }>(
      `/api/sales/deals/${dealUuid}/scope-driver-suggestions`
    );
    return res.suggestions;
  },

  /**
   * Inline AI "enhance" of one scope-brief dimension from the user's draft (background, not chat):
   * rewrites a text dimension (`{ text }`) or proposes chips for a tag dimension (`{ suggestions }`).
   */
  async enhanceScopeText(
    dealUuid: string,
    fieldKey: string,
    dimension: ScopeBriefKey,
    text: string
  ): Promise<{ text?: string; suggestions?: string[] }> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/enhance`,
      { method: 'POST', body: JSON.stringify({ dimension, text }) }
    );
  },

  /**
   * Generate the ranked top-2-3 candidate solutions for one AI-captured scope
   * question (BPR deals - grounded in the engagement's framework, corpus, and
   * this deal's transcripts). Long-running (one thinking-tier model call);
   * the server persists the batch onto scope_meta.extra so it survives reloads.
   */
  async suggestSolutionCandidates(
    dealUuid: string,
    fieldKey: string
  ): Promise<{ batch: BprSolutionCandidateBatch }> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/solution-candidates`,
      { method: 'POST' }
    );
  },

  /** Stamp which candidate the consultant chose ("Use this solution"). Marker only. */
  async pickSolutionCandidate(
    dealUuid: string,
    fieldKey: string,
    candidateId: string
  ): Promise<void> {
    await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/solution-candidates/pick`,
      { method: 'POST', body: JSON.stringify({ candidateId }) }
    );
  },

  /** Sections + groups a field can be placed in (for promoting an ad-hoc question). */
  async getTemplatePlacement(dealUuid: string): Promise<{
    sections: Array<{
      sectionKey: string;
      label: string;
      groups: Array<{ groupKey: string; label: string }>;
    }>;
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/template/placement`);
  },

  /**
   * Persist a new display order for the deal's primary ad-hoc scope items (drag-to-reorder in the
   * Prioritization pane). `orderedFieldKeys` must be a complete permutation of the deal's primary
   * fieldKeys; the new order is reflected in both the Prioritization pane and the questionnaire.
   */
  async reorderAdhocFields(dealUuid: string, orderedFieldKeys: string[]): Promise<void> {
    await apiFetch(`/api/sales/deals/${dealUuid}/adhoc-fields/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedFieldKeys }),
    });
  },

  /** Remove a one-time ad-hoc question (AI-captured or manual) and its answer from a deal. */
  async deleteAdhocField(dealUuid: string, fieldKey: string): Promise<void> {
    await apiFetch(`/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}`, {
      method: 'DELETE',
    });
  },

  /** Promote a one-time ad-hoc question into a permanent template field at the given placement. */
  async promoteAdhocField(
    dealUuid: string,
    fieldKey: string,
    placement: { sectionKey: string; groupKey: string }
  ): Promise<{ field: { uuid: string; fieldKey: string; label: string } }> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/adhoc-fields/${encodeURIComponent(fieldKey)}/promote`,
      { method: 'POST', body: JSON.stringify(placement) }
    );
  },

  async getSampleDealData(
    dealUuid: string
  ): Promise<{ answers: Array<{ fieldKey: string; value: unknown }> }> {
    return await apiFetch(`/api/dev/deal-sample-data?dealUuid=${encodeURIComponent(dealUuid)}`);
  },

  async getDealEstimate(dealUuid: string): Promise<SalesDealEstimateResult> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate`);
  },

  /**
   * Live staffing-rate coverage for a deal (COS-TBD mcp-missing-staffing). The deal page
   * reads it before generating or exporting priced output; `blocking` means the plan would
   * price at 0 h / $0, and the API refuses those routes with 409 STAFFING_RATES_MISSING.
   */
  async getStaffingCoverage(dealUuid: string): Promise<StaffingCoverage> {
    return await apiFetch(`/api/sales/deals/${encodeURIComponent(dealUuid)}/staffing-coverage`);
  },

  async getSavedEstimate(dealUuid: string): Promise<{ savedEstimate: SavedDealEstimate | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/saved`);
  },

  async saveEstimate(dealUuid: string): Promise<{ savedEstimate: SavedDealEstimate }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/save`, { method: 'POST' });
  },

  /**
   * Upload a FINALIZED estimate file to a Continuous Improvement deal (the upload-CI-SOW
   * bridge): the server parses it, converts the line items into the deal's own estimate, and
   * saves the estimate + resource plan. A re-upload replaces the previously imported items.
   */
  async uploadDealEstimate(
    dealUuid: string,
    file: File
  ): Promise<{
    savedEstimate: SavedDealEstimate;
    resourcePlan: DealResourcePlan | null;
    importedItemCount: number;
    replacedItemCount: number;
    title?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/upload`, {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Parse an uploaded scope list (docs/plans/27) WITHOUT writing anything: returns the detected
   * column mapping, a short preview, and what the deal already carries, for the confirm step.
   */
  async parseDealScopeFile(
    dealUuid: string,
    file: File,
    sheetIndex?: number
  ): Promise<ScopeFileParseResponse> {
    const formData = new FormData();
    if (sheetIndex != null) formData.append('sheetIndex', String(sheetIndex));
    formData.append('file', file, file.name);
    return await apiFetch(`/api/sales/deals/${dealUuid}/scope-file/parse`, {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Parse a scope list BEFORE a deal exists (the create-deal flow). Workspace-scoped, writes
   * nothing - so an unreadable file never leaves a half-created deal behind.
   */
  async parseScopeFileForWorkspace(file: File, sheetIndex?: number): Promise<ScopeFileParseResponse> {
    const formData = new FormData();
    if (sheetIndex != null) formData.append('sheetIndex', String(sheetIndex));
    formData.append('file', file, file.name);
    return await apiFetch('/api/sales/scope-file/parse', { method: 'POST', body: formData });
  },

  /**
   * Import the confirmed scope list onto the deal. The SAME file is re-posted with the mapping the
   * user accepted or corrected (the server keeps no parse cache), and rows are created with NO
   * hours - the caller then runs the normal estimate generation, which prices them.
   */
  async importDealScopeFile(
    dealUuid: string,
    file: File,
    confirmed: { sheetIndex: number; headerRow: number; mapping: Record<string, number> }
  ): Promise<ScopeFileImportResponse> {
    const formData = new FormData();
    formData.append('mapping', JSON.stringify(confirmed));
    formData.append('file', file, file.name);
    return await apiFetch(`/api/sales/deals/${dealUuid}/scope-file/import`, {
      method: 'POST',
      body: formData,
    });
  },

  async fetchTemplateContract(key: string, dealUuid?: string): Promise<{ contract: unknown }> {
    const url = new URL(`/api/sales/templates/${encodeURIComponent(key)}`, window.location.origin);
    if (dealUuid) {
      url.searchParams.set('dealUuid', dealUuid);
    }
    return await apiFetch(url.pathname + url.search);
  },

  async getEstimateConfig(): Promise<{
    hasDriveIntegration: boolean;
    hasResourcePlanDriveIntegration: boolean;
    hasSowDriveIntegration: boolean;
  }> {
    return await apiFetch('/api/sales/estimate/config');
  },

  // Versions default to the current estimate; pass estimateUuid to act on a past one.
  async listEstimateVersions(dealUuid: string): Promise<{ versions: EstimateVersionSummary[] }> {
    return await apiFetch(`/api/sales/deals/${encodeURIComponent(dealUuid)}/estimate/versions`);
  },

  async getEstimateVersion(
    dealUuid: string,
    estimateUuid: string
  ): Promise<{ savedEstimate: SavedDealEstimate }> {
    return await apiFetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/estimate/versions/${encodeURIComponent(estimateUuid)}`
    );
  },

  async exportEstimateDrive(dealUuid: string, estimateUuid?: string): Promise<{ url: string }> {
    const suffix = estimateUuid ? `&estimateUuid=${encodeURIComponent(estimateUuid)}` : '';
    return await apiFetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/estimate/export?target=drive${suffix}`
    );
  },

  async exportResourcePlanDrive(dealUuid: string, plan: any): Promise<{ url: string }> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const { generateGanttImage, generateTimelineImage } = await import('@protoss/chart');
    const { computeDisplayPhases } = await import('../components/DealResourcePlanPane');

    // Yield to allow the browser to paint 'Exporting...'
    await new Promise((r) => setTimeout(r, 50));

    const snap = plan.resourcingSnapshot;
    const showTimeline = snap.display?.show_timeline ?? true;
    const showGantt = snap.display?.show_gantt ?? true;
    const displayPhases = computeDisplayPhases(snap);
    // Span = furthest end week (matches the pane). Equals sum-of-weeks for
    // waterfall; shorter when phases overlap.
    const displayTotalWeeks = displayPhases.reduce(
      (max: number, p: any) => Math.max(max, p.startWeek + p.weeks - 1),
      1
    );

    const timelineBase64 = showTimeline
      ? generateTimelineImage(displayPhases, displayTotalWeeks)
      : undefined;
    const ganttBase64 = showGantt
      ? generateGanttImage(displayPhases, snap.staffing_plan as any, displayTotalWeeks)
      : undefined;

    const idToken = await getAuthToken();
    const res = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/export/resource-plan?target=drive`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          plan,
          ...(timelineBase64 ? { timelineBase64, timelineWidth: 1000, timelineHeight: 140 } : {}),
          ...(ganttBase64
            ? { ganttBase64, ganttWidth: 1000, ganttHeight: 60 + displayPhases.length * 45 + 80 }
            : {}),
          showTimeline,
          showGantt,
        }),
      }
    );
    if (!res.ok) {
      const errorBody = await res.text().catch(() => '');
      throw new Error(`Export failed: ${res.status} - ${errorBody}`);
    }
    return res.json();
  },

  async exportResourcePlanDocx(dealUuid: string, plan: any): Promise<void> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const { generateGanttImage, generateTimelineImage } = await import('@protoss/chart');
    const { computeDisplayPhases } = await import('../components/DealResourcePlanPane');

    // Yield to allow the browser to paint 'Exporting...'
    await new Promise((r) => setTimeout(r, 50));

    const snap = plan.resourcingSnapshot;
    const showTimeline = snap.display?.show_timeline ?? true;
    const showGantt = snap.display?.show_gantt ?? true;
    const displayPhases = computeDisplayPhases(snap);
    // Span = furthest end week (matches the pane). Equals sum-of-weeks for
    // waterfall; shorter when phases overlap.
    const displayTotalWeeks = displayPhases.reduce(
      (max: number, p: any) => Math.max(max, p.startWeek + p.weeks - 1),
      1
    );

    const timelineBase64 = showTimeline
      ? generateTimelineImage(displayPhases, displayTotalWeeks)
      : undefined;
    const ganttBase64 = showGantt
      ? generateGanttImage(displayPhases, snap.staffing_plan as any, displayTotalWeeks)
      : undefined;

    const idToken = await getAuthToken();
    const response = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/export/resource-plan`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          plan,
          ...(timelineBase64 ? { timelineBase64, timelineWidth: 1000, timelineHeight: 140 } : {}),
          ...(ganttBase64
            ? { ganttBase64, ganttWidth: 1000, ganttHeight: 60 + displayPhases.length * 45 + 80 }
            : {}),
          showTimeline,
          showGantt,
        }),
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `Failed to export: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers.get('content-disposition');
    let filename = 'resource-plan.docx';
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches?.[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  // Versions default to the current SOW; pass sowUuid to act on a past one.
  async listSowVersions(dealUuid: string): Promise<{ versions: SowVersionSummary[] }> {
    return await apiFetch(`/api/sales/deals/${encodeURIComponent(dealUuid)}/sow/versions`);
  },

  async getSowVersion(dealUuid: string, sowUuid: string): Promise<{ sow: DealSow }> {
    return await apiFetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/sow/versions/${encodeURIComponent(sowUuid)}`
    );
  },

  async exportSowDrive(dealUuid: string, sowUuid?: string): Promise<{ url: string }> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const suffix = sowUuid ? `&sowUuid=${encodeURIComponent(sowUuid)}` : '';
    const res = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/export/sow?target=drive${suffix}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({}),
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Export failed: ${res.status} - ${body}`);
    }
    return res.json();
  },

  async exportSowDocx(dealUuid: string, sowUuid?: string): Promise<void> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const query = sowUuid ? `?sowUuid=${encodeURIComponent(sowUuid)}` : '';
    const response = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/export/sow${query}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({}),
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `Failed to export: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers.get('content-disposition');
    let filename = 'statement-of-work.docx';
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches?.[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async exportEstimateXlsx(dealUuid: string, estimateUuid?: string): Promise<void> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const query = estimateUuid ? `?estimateUuid=${encodeURIComponent(estimateUuid)}` : '';
    const res = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/estimate/export${query}`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[exportEstimateXlsx] Server error body:', body);
      throw new Error(`Export failed: ${res.status} - ${body}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? 'estimate.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Download the Prioritization Matrix as a branded .xlsx (same flow as exportEstimateXlsx). */
  async exportPrioritizationXlsx(dealUuid: string): Promise<void> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const res = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/prioritization/export`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[exportPrioritizationXlsx] Server error body:', body);
      throw new Error(`Export failed: ${res.status} - ${body}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? 'prioritization-matrix.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Fetch the Jira ticket plan for a deal (Step 1 preview): one flat ticket per costed scope item,
   * plus totals and warnings. Drives the "Export to Jira" drawer's validation table.
   */
  async getJiraTicketPlan(dealUuid: string): Promise<JiraTicketPlan> {
    return await apiFetch(`/api/sales/deals/${encodeURIComponent(dealUuid)}/jira-export/plan`);
  },

  /** List the workspace's connected Jira credentials (for the sync target picker). */
  async listJiraConnections(): Promise<Array<{ uuid: string; displayName: string }>> {
    const { integrations } = (await apiFetch('/api/integration')) as {
      integrations: Array<{ uuid: string; displayName: string; definition: { slug: string } }>;
    };
    return integrations
      .filter((i) => i.definition?.slug === 'jira')
      .map((i) => ({ uuid: i.uuid, displayName: i.displayName }));
  },

  /** List the Jira projects available to a credential (drives the project picker). */
  async listJiraProjects(credentialUuid: string): Promise<Array<{ id: string; name: string }>> {
    const { resources } = (await apiFetch(
      `/api/integration/${encodeURIComponent(credentialUuid)}/resources?type=list_projects`
    )) as { resources: Array<{ id: string; name: string }> };
    return resources.map((r) => ({ id: r.id, name: r.name }));
  },

  /** Reconcile + push the deal's tickets to Jira (create/update/close). Idempotent - no duplicates. */
  async syncJiraTickets(
    dealUuid: string,
    req: { credentialUuid: string; projectKey?: string; containerType?: 'epic' | 'project' }
  ): Promise<JiraSyncSummary> {
    return await apiFetch(`/api/sales/deals/${encodeURIComponent(dealUuid)}/jira-export/sync`, {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  /** Download the Jira ticket list as a branded .xlsx (same flow as exportPrioritizationXlsx). */
  async exportJiraTicketsXlsx(dealUuid: string): Promise<void> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const res = await fetch(
      `/api/sales/deals/${encodeURIComponent(dealUuid)}/jira-export/tickets.xlsx`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[exportJiraTicketsXlsx] Server error body:', body);
      throw new Error(`Export failed: ${res.status} - ${body}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? 'jira-tickets.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  async patchEstimateOverride(
    dealUuid: string,
    key: string,
    overrideHours: number | null,
    overrideReason?: string | null
  ): Promise<{ savedEstimate: SavedDealEstimate }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/overrides`, {
      method: 'PATCH',
      body: JSON.stringify({ key, overrideHours, overrideReason }),
    });
  },

  async patchEstimateDiscountOverride(
    dealUuid: string,
    discountPercentageOverride: number | null
  ): Promise<{ savedEstimate: SavedDealEstimate }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/discount`, {
      method: 'PATCH',
      body: JSON.stringify({ discountPercentageOverride }),
    });
  },

  /**
   * Persist a user inline-edit of a subsection's AI summary (the estimate card's editable
   * "AI Summary" / "Raw Output" text). `key` follows "subsection:{sectionKey}:{subsectionKey}".
   * Omit a field to leave it unchanged; pass a string to set it (empty/null clears it).
   */
  async patchEstimateSummary(
    dealUuid: string,
    key: string,
    patch: { customerSummary?: string | null; aiSummary?: string | null }
  ): Promise<{ savedEstimate: SavedDealEstimate }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/summary`, {
      method: 'PATCH',
      body: JSON.stringify({ key, ...patch }),
    });
  },

  /**
   * Inline AI "Suggest" of a subsection summary from the user's draft (background, not chat):
   * `kind: 'customer'` rewrites the client-facing summary, `kind: 'ai'` the technical rationale.
   */
  async suggestEstimateSummary(
    dealUuid: string,
    key: string,
    kind: 'customer' | 'ai',
    text: string
  ): Promise<{ text?: string }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/estimate/summary/suggest`, {
      method: 'POST',
      body: JSON.stringify({ key, kind, text }),
    });
  },

  async getResourcePlan(dealUuid: string): Promise<{ plan: DealResourcePlan | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan`);
  },

  async getRateCardProfiles(dealUuid: string): Promise<{
    profiles: Array<{ id: number; version: string; isDefault: boolean; scope: string }>;
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/rate-card-profiles`);
  },

  async generateResourcePlan(
    dealUuid: string,
    rateCardProfileId?: number
  ): Promise<{ plan: DealResourcePlan | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan/generate`, {
      method: 'POST',
      body: rateCardProfileId ? JSON.stringify({ rateCardProfileId }) : undefined,
    });
  },

  async updateResourcePlanPricingBasis(
    dealUuid: string,
    roleId: string,
    pricingBasis: ResourcePlanPricingBasis,
    offshorePercent?: number
  ): Promise<{ plan: DealResourcePlan | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan/pricing-basis`, {
      method: 'PATCH',
      body: JSON.stringify({ roleId, pricingBasis, offshorePercent }),
    });
  },

  async updateResourcePlanRoleAllocation(
    dealUuid: string,
    roleId: string,
    allocationPercent: number | null
  ): Promise<{ plan: DealResourcePlan | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan/allocation`, {
      method: 'PATCH',
      body: JSON.stringify({ roleId, allocationPercent }),
    });
  },

  // The full workspace role catalog for the role multi-select (not the AI priority roles).
  async listResourcePlanAvailableRoles(
    dealUuid: string
  ): Promise<{ roles: ResourcePlanAvailableRole[] }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan/available-roles`);
  },

  // Set the deal's role roster (full desired selection of role ids).
  async setResourcePlanRoles(
    dealUuid: string,
    roleIds: string[]
  ): Promise<{ plan: DealResourcePlan | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/resource-plan/roles`, {
      method: 'PATCH',
      body: JSON.stringify({ roleIds }),
    });
  },

  async getSow(dealUuid: string): Promise<{ sow: DealSow | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow`);
  },

  async getSowRenderContext(dealUuid: string): Promise<SowRenderContext> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/render-context`);
  },

  /**
   * Direct SOW generation (POST /sow/generate) - the managed_services questionnaire
   * wizard's Preview SOW button. Static-mode categories generate deterministically
   * (no AI), so this returns fast. Raw fetch instead of apiFetch: a 422
   * SOW_MISSING_REQUIRED_FIELDS response carries the readinessReport in its body,
   * which apiFetch discards - it is re-thrown as SowReadinessError so callers can
   * render the blocker list.
   */
  async generateSow(dealUuid: string): Promise<{ sow: DealSow }> {
    const { getAuthToken } = await import('../../../lib/apiClient');
    const idToken = await getAuthToken();
    const response = await fetch(`/api/sales/deals/${dealUuid}/sow/generate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string | { code?: string; message?: string };
        message?: string;
        readinessReport?: SowReadinessReport;
      };
      if (body.error === 'SOW_MISSING_REQUIRED_FIELDS' && body.readinessReport) {
        throw new SowReadinessError(body.readinessReport);
      }
      const base =
        (typeof body.error === 'object' ? body.error?.message : body.error) ??
        body.message ??
        `Request failed: ${response.status}`;
      throw new Error(base);
    }
    return (await response.json()) as { sow: DealSow };
  },

  async regenerateSow(dealUuid: string): Promise<DealSowGenerationResult> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/regenerate`, { method: 'POST' });
  },

  async pollExtractionRun(runId: number): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed';
    extractions?: unknown[];
    error?: string;
  }> {
    return await apiFetch(`/api/sales/deals/extract-runs/${runId}`);
  },

  async updateDealStatus(
    dealUuid: string,
    status: 'draft' | 'ready_for_generation' | 'generated'
  ): Promise<{ deal: SalesDealSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  async updateDealTitle(dealUuid: string, title: string): Promise<{ deal: SalesDealSummary }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/title`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },

  async getDealAttributes(dealUuid: string): Promise<{
    data: Array<{
      id: number;
      attributeKey: string;
      label: string;
      displayOrder: number;
      isSystem: boolean;
      currentValue: string | null;
      options: Array<{
        id: number;
        valueKey: string;
        label: string;
        color: string | null;
        displayOrder: number;
      }>;
    }>;
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/attributes`);
  },

  async updateDealAttribute(
    dealUuid: string,
    attributeKey: string,
    valueKey: string | null
  ): Promise<{
    data: {
      dealTypeKey: string | null;
      dealCategoryKey: string | null;
      dealStatusKey: string | null;
    };
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/attributes`, {
      method: 'PATCH',
      body: JSON.stringify({ attributeKey, valueKey }),
    });
  },

  async updateSow(
    dealUuid: string,
    sowUuid: string,
    payload: { tiptapContent: Record<string, unknown> } | { sowContent: string }
  ): Promise<{ sow: DealSow }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/${sowUuid}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /** Save a manual override for one descriptive SOW data table (Data Conversion / Integrated Partners). */
  async saveSowDataOverride(
    dealUuid: string,
    sowUuid: string,
    dataKey: string,
    rows: Array<Record<string, string>>
  ): Promise<{ sow: DealSow }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/${sowUuid}/data-override`, {
      method: 'PUT',
      body: JSON.stringify({ dataKey, rows }),
    });
  },

  /** Clear a SOW data-table override (reset to auto-filled data). */
  async resetSowDataOverride(
    dealUuid: string,
    sowUuid: string,
    dataKey: string
  ): Promise<{ sow: DealSow }> {
    return await apiFetch(
      `/api/sales/deals/${dealUuid}/sow/${sowUuid}/data-override/${encodeURIComponent(dataKey)}`,
      { method: 'DELETE' }
    );
  },

  /** Remove (`excluded:true`) or restore (`excluded:false`) a conditional SOW section. */
  async setSowExcludedSection(
    dealUuid: string,
    sowUuid: string,
    sectionSlug: string,
    excluded: boolean
  ): Promise<{ sow: DealSow }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/${sowUuid}/excluded-section`, {
      method: 'PUT',
      body: JSON.stringify({ sectionSlug, excluded }),
    });
  },

  /** Set (`value` string) or clear (`value:null`) a manual token override for this SOW. */
  async setSowTokenOverride(
    dealUuid: string,
    sowUuid: string,
    tokenPath: string,
    value: string | null
  ): Promise<{ sow: DealSow }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/sow/${sowUuid}/token-override`, {
      method: 'PUT',
      body: JSON.stringify({ tokenPath, value }),
    });
  },

  async listDealDocuments(dealUuid: string): Promise<{ documents: CustomerDocument[] }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/documents`);
  },

  // ── Meeting context (Docs tab: last-meeting recap + next-meeting prep) ──────
  async getMeetingContext(
    dealUuid: string
  ): Promise<{ current: SalesDealMeetingContext | null; generating: boolean }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/meeting-context`);
  },

  async regenerateMeetingContext(
    dealUuid: string
  ): Promise<{ current: SalesDealMeetingContext | null }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/meeting-context/regenerate`, {
      method: 'POST',
    });
  },

  async getDealDocActivity(dealUuid: string): Promise<{ groups: DealDocActivityGroup[] }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/doc-activity`);
  },

  async getDealDocument(
    dealUuid: string,
    docUuid: string
  ): Promise<{ document: CustomerDocumentDetail }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/documents/${docUuid}`);
  },

  async extractLiveMeeting(
    dealUuid: string,
    body: {
      recentChunks: Array<{ userName: string; text: string; role?: 'host' | 'internal' | 'guest' }>;
      suggestedFieldKeys?: string[];
      hostName?: string | null;
    }
  ): Promise<{
    results: Array<
      | { type: 'question_suggestion'; fieldKey: string; suggestedQuestion: string }
      | { type: 'answer_extracted'; fieldKey: string; answer: string }
    >;
  }> {
    return await apiFetch(`/api/sales/deals/${dealUuid}/live-meeting/extract`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getLiveMeetingQuestionnaire(): Promise<{ questionnaire: SalesDealQuestionnaireSummary }> {
    return await apiFetch('/api/sales/live-meeting/questionnaire');
  },

  async extractLiveMeetingDefault(body: {
    recentChunks: Array<{ userName: string; text: string; role?: 'host' | 'internal' | 'guest' }>;
    hostName?: string | null;
  }): Promise<{
    results: Array<
      | { type: 'question_suggestion'; fieldKey: string; suggestedQuestion: string }
      | { type: 'answer_extracted'; fieldKey: string; answer: string }
    >;
  }> {
    return await apiFetch('/api/sales/live-meeting/extract', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async suggestLiveMeetingQuestions(body: {
    recentSegments: Array<{ speakerName: string; text: string }>;
    sections: Array<{
      label: string;
      fieldKeys?: string[];
      fieldLabels: string[];
      fieldDescriptions?: string[];
    }>;
    currentSuggestions?: string[];
    hostName?: string | null;
    sessionUuid?: string | null;
  }): Promise<{
    suggestions: Array<{ text: string; sectionLabel: string; fieldKey: string | null }>;
    addressed: string[];
  }> {
    return await apiFetch('/api/sales/live-meeting/suggest', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async linkLiveMeetingDeal(
    sessionUuid: string,
    body: { dealUuid: string }
  ): Promise<{ sessionUuid: string; dealUuid: string; dealTitle: string }> {
    return await apiFetch(`/api/sales/live-meeting-sessions/${sessionUuid}/deal`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
};
